import { Collapse } from "bootstrap";

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export class Drawer {
    drawer: HTMLElement;
    toggle: HTMLButtonElement | null;
    background: HTMLElement | null;

    private open = false;
    private collapseInstance: Collapse;

    constructor(
        drawerSelector = "#drawer",
        toggleSelector = ".drawer-toggle",
        backgroundSelector = ".drawer-background",
    ) {
        this.drawer = document.querySelector(drawerSelector) as HTMLElement;
        this.toggle = document.querySelector(toggleSelector) as HTMLButtonElement | null;
        this.background = document.querySelector(backgroundSelector) as HTMLElement | null;

        this.open = this.drawer.classList.contains("show");

        this.collapseInstance = Collapse.getOrCreateInstance(this.drawer, { toggle: false });
        this.bindBootstrapEvents();

        this.background?.addEventListener("click", () => this.collapseInstance.hide());
        document.addEventListener("keydown", this.onKeyDown);

        this.syncA11yState();
    }

    private bindBootstrapEvents(): void {
        // Mark open as soon as opening starts (drawer is "logically open" during .collapsing)
        this.drawer.addEventListener("show.bs.collapse", () => {
            this.open = true;
            this.drawer.classList.add("active");
            this.syncA11yState();
        });

        this.drawer.addEventListener("shown.bs.collapse", () => {
            // Move focus into the drawer so Tab stays inside it
            this.getFocusables()[0]?.focus();
        });

        // On hide start, return focus immediately (don't leave focus inside a collapsing region)
        this.drawer.addEventListener("hide.bs.collapse", () => {
            this.open = false;
            this.drawer.classList.remove("active");
            this.toggle?.focus();
            // Keep it reachable until it's actually hidden
            this.syncA11yState();
        });

        // After fully hidden, make it inert
        this.drawer.addEventListener("hidden.bs.collapse", () => {
            this.open = false;
            this.syncA11yState();
        });
    }

    private isOpen(): boolean {
        return this.open;
    }

    private getFocusables(): HTMLElement[] {
        return Array.from(this.drawer.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.tabIndex !== -1);
    }

    private syncA11yState(): void {
        const open = this.isOpen();
        this.drawer.toggleAttribute("inert", !open);
        this.drawer.setAttribute("aria-hidden", open ? "false" : "true");
    }

    private trapFocus(e: KeyboardEvent): void {
        if (e.key !== "Tab") return;

        const focusables = this.getFocusables();
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        } else if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
        }
    }

    private moveFocus(direction: 1 | -1): void {
        const focusables = this.getFocusables();
        if (focusables.length === 0) return;

        const active = document.activeElement as HTMLElement | null;
        const currentIndex = focusables.indexOf(active as HTMLElement);

        const nextIndex =
            currentIndex === -1
                ? direction === 1
                    ? 0
                    : focusables.length - 1
                : (currentIndex + direction + focusables.length) % focusables.length;

        focusables[nextIndex].focus();
    }

    private onKeyDown = (e: KeyboardEvent): void => {
        if (!this.isOpen()) return;

        if (e.key === "Escape") {
            e.preventDefault();
            this.collapseInstance.hide();
            return;
        }

        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            this.moveFocus(e.key === "ArrowDown" ? 1 : -1);
            return;
        }

        this.trapFocus(e);
    };
}
